// ── Column types & table-level constants ────────────────────────────────────

export interface ColumnDef {
  key: string;
  label: string;
  value: string;
  isSortable: boolean;
  isChecked: boolean;
  isPinned: boolean;
  isDisabled: boolean;
  width: string;
  isColumnConfigEnabled: boolean;
}

export interface AssignedRole {
  roleName: string;
  fueScore: number;
}

export interface AssignedRoleGroup {
  endpointName: string;
  tierLabel: string;
  totalRoles: number;
  maxFue: number;
  roles: AssignedRole[];
}

export interface FUEInventoryRow {
  id: string;
  userId: string;
  department: string;
  identityType: string;
  currentTier: string;
  currentFue?: number;
  recommendedTier: string;
  evaluatedCost: number;
  evaluationStatus:
    | 'Compliant'
    | 'Over-licensed'
    | 'Dormant'
    | 'Classification Mismatch';
  lastActiveAt: string;
  daysInactive: number;
  assignedRoles: AssignedRoleGroup[];
}

export const COLUMN_WIDTH = '12.25rem';

function makeCol(
  key: string,
  label: string,
  width: string,
  isSortable = true,
  isPinned = false,
  isColumnConfigEnabled = true
): ColumnDef {
  return {
    key,
    label,
    value: key,
    isSortable,
    isChecked: true,
    isPinned,
    isDisabled: false,
    width,
    isColumnConfigEnabled,
  };
}

function makeOptionalCol(
  key: string,
  label: string,
  width: string,
  isSortable = true
): ColumnDef {
  return {
    key,
    label,
    value: key,
    isSortable,
    isChecked: false,
    isPinned: false,
    isDisabled: false,
    width,
    isColumnConfigEnabled: true,
  };
}

export const FUE_ACTION_COLUMN: ColumnDef = {
  key: 'actionColumn',
  label: 'actionColumn',
  value: 'actionColumn',
  isSortable: false,
  isPinned: true,
  isColumnConfigEnabled: true,
  isDisabled: true,
  isChecked: true,
  width: '3rem',
};

/**
 * API fields already represented in other columns and must not auto-generate
 * a duplicate column:
 *   identityId  → used as the row id
 *   accountKey  → internal key, not displayed
 *   displayName → shown as userId for Human
 *   accountName → shown as userId for non-Human
 *
 * Any other field that comes from the API — now or in the future — is
 * automatically added as an optional column in the config picker.
 * Object/array fields (e.g. unitAnalysis) are skipped via a runtime
 * typeof check in buildColumns / the row mapping; no list needed here.
 */
export const ROW_MAPPED_API_FIELDS = new Set([
  'identityId',
  'accountKey',
  'displayName',
  'accountName',
  'identityType',
  'departmentName',
]);

// camelCase → "UPPER CASE WORDS"  e.g. currentUnit → "CURRENT UNIT"
function toColumnLabel(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .toUpperCase()
    .trim();
}

// Mandatory (always-visible) columns per category.
// All other primitive fields that arrive in the API response are auto-added
// as optional columns at runtime by buildColumns.
const NON_HUMAN_FIRST_COL = makeCol(
  'jobId',
  'ACCOUNT NAME',
  COLUMN_WIDTH,
  true,
  true,
  true
);

const MANDATORY_COLS_BY_CATEGORY: Record<string, ColumnDef[]> = {
  Human: [
    makeCol('userId', 'USER ID', COLUMN_WIDTH, true, true, true),
    makeCol('department', 'DEPARTMENT', COLUMN_WIDTH),
    makeCol('currentTier', 'CURRENT TIER', COLUMN_WIDTH),
    makeCol('recommendedTier', 'RECOMMENDED TIER', COLUMN_WIDTH),
    makeCol('evaluationStatus', 'STATUS', COLUMN_WIDTH),
    makeCol('evaluatedCost', 'COST', COLUMN_WIDTH, false),
  ],
  'Service Account': [
    NON_HUMAN_FIRST_COL,
    makeCol('currentTier', 'CURRENT TIER', COLUMN_WIDTH),
    makeCol('recommendedTier', 'RECOMMENDED TIER', COLUMN_WIDTH),
    makeCol('evaluationStatus', 'STATUS', COLUMN_WIDTH),
    makeCol('evaluatedCost', 'COST', COLUMN_WIDTH, false),
    makeCol('lastActiveAt', 'LAST ACTIVE', COLUMN_WIDTH, true),
  ],
  Emergency: [
    NON_HUMAN_FIRST_COL,
    makeCol('department', 'DEPARTMENT', COLUMN_WIDTH),
    makeCol('evaluationStatus', 'STATUS', COLUMN_WIDTH),
    makeCol('evaluatedCost', 'COST', COLUMN_WIDTH, false),
    makeCol('lastActiveAt', 'LAST ACTIVE', COLUMN_WIDTH, true),
  ],
  Batch: [
    NON_HUMAN_FIRST_COL,
    makeCol('currentTier', 'CURRENT TIER', COLUMN_WIDTH),
    makeCol('recommendedTier', 'RECOMMENDED TIER', COLUMN_WIDTH),
    makeCol('evaluationStatus', 'STATUS', COLUMN_WIDTH),
    makeCol('evaluatedCost', 'COST', COLUMN_WIDTH, false),
    makeCol('lastActiveAt', 'LAST ACTIVE', COLUMN_WIDTH, true),
  ],
  Communication: [
    NON_HUMAN_FIRST_COL,
    makeCol('currentTier', 'CURRENT TIER', COLUMN_WIDTH),
    makeCol('recommendedTier', 'RECOMMENDED TIER', COLUMN_WIDTH),
    makeCol('evaluationStatus', 'STATUS', COLUMN_WIDTH),
    makeCol('evaluatedCost', 'COST', COLUMN_WIDTH, false),
    makeCol('lastActiveAt', 'LAST ACTIVE', COLUMN_WIDTH, true),
  ],
  Orphan: [
    NON_HUMAN_FIRST_COL,
    makeCol('currentTier', 'CURRENT TIER', COLUMN_WIDTH),
    makeCol('recommendedTier', 'RECOMMENDED TIER', COLUMN_WIDTH),
    makeCol('evaluationStatus', 'STATUS', COLUMN_WIDTH),
    makeCol('evaluatedCost', 'COST', COLUMN_WIDTH, false),
    makeCol('lastActiveAt', 'LAST ACTIVE', COLUMN_WIDTH, true),
  ],
  'AI Agent': [
    NON_HUMAN_FIRST_COL,
    makeCol('currentTier', 'CURRENT TIER', COLUMN_WIDTH),
    makeCol('recommendedTier', 'RECOMMENDED TIER', COLUMN_WIDTH),
    makeCol('evaluationStatus', 'STATUS', COLUMN_WIDTH),
    makeCol('evaluatedCost', 'COST', COLUMN_WIDTH, false),
    makeCol('lastActiveAt', 'LAST ACTIVE', COLUMN_WIDTH, true),
  ],
};

/**
 * Builds the full column list for a category.
 * presentApiFields: all primitive top-level field names seen in the live API
 * response for this category (object/array-valued fields excluded by the
 * caller before passing here).
 * Any field not already in the mandatory set and not in ROW_MAPPED_API_FIELDS
 * becomes an optional column automatically.
 */
export function buildColumns(
  category: string,
  presentApiFields: Set<string>
): ColumnDef[] {
  const base = MANDATORY_COLS_BY_CATEGORY[category] ?? [];
  const mandatoryKeys = new Set(base.map((c) => c.key));

  const dynamicOptional = [...presentApiFields]
    .filter((key) => !mandatoryKeys.has(key) && !ROW_MAPPED_API_FIELDS.has(key))
    .map((key) =>
      makeOptionalCol(key, toColumnLabel(key), COLUMN_WIDTH, false)
    );

  return [...base, ...dynamicOptional, FUE_ACTION_COLUMN];
}

// Fallback columns before API data arrives — mandatory cols only, no optional.
export const COLUMNS_BY_CATEGORY: Record<string, ColumnDef[]> =
  Object.fromEntries(
    Object.keys(MANDATORY_COLS_BY_CATEGORY).map((cat) => [
      cat,
      [...(MANDATORY_COLS_BY_CATEGORY[cat] ?? []), FUE_ACTION_COLUMN],
    ])
  );

export const IDENTITY_CATEGORIES = [
  'Human',
  'Service Account',
  'Emergency',
  'Communication',
  'Orphan',
  'AI Agent',
] as const;

// ── Sort options ────────────────────────────────────────────────────────────

export const SORT_OPTIONS = [
  {
    label: 'Ascending',
    value: 'asc',
    isDisabled: false,
    isChecked: false,
    icon: 'ArrowDown',
    shouldShowCheckIcon: true,
  },
  {
    label: 'Descending',
    value: 'desc',
    isDisabled: false,
    isChecked: false,
    icon: 'ArrowUp',
    shouldShowCheckIcon: true,
  },
];

// ── Filter types ────────────────────────────────────────────────────────────

export interface FueFilterOption {
  value: string;
  label: string;
}

export interface FueFilterDef {
  id: string;
  /** Original plural API key (e.g. "statuses"). Used when sending filters to the search endpoint. */
  apiKey?: string;
  label: string;
  category: string;
  options: FueFilterOption[];
}

// ── API filter metadata ─────────────────────────────────────────────────────
// Maps each API response key (e.g. "statuses") to a human-readable chip label
// and add/remove menu category. Keys not listed here are still handled — their
// label is auto-derived from the key (camelCase → Title Case) and category
// defaults to 'Other'. Add a new entry here only to override that auto-label.

export const API_FILTER_LABELS: Record<string, string> = {
  statuses: 'Status',
  tiers: 'Tier',
  departments: 'Department',
};

export const API_FILTER_CATEGORIES: Record<string, string> = {
  statuses: 'Identity',
  tiers: 'License',
  departments: 'User',
};

// ── Identity-specific filters ───────────────────────────────────────────────
// These filters have no API-provided options — their dropdown values are built
// from mock data (dynamicFilterOptions). Each entry only appears for its
// designated identity category.

const JOB_ID_FILTER: FueFilterDef = {
  id: 'jobId',
  label: 'Job ID',
  category: 'Identity',
  options: [],
};

export const IDENTITY_FILTERS_BY_CATEGORY: Record<string, FueFilterDef[]> = {
  Human: [{ id: 'userId', label: 'User ID', category: 'User', options: [] }],
  'Service Account': [JOB_ID_FILTER],
  Emergency: [JOB_ID_FILTER],
  Batch: [JOB_ID_FILTER],
  Communication: [JOB_ID_FILTER],
  Orphan: [JOB_ID_FILTER],
  'AI Agent': [JOB_ID_FILTER],
};

// ── Locked & default filter state ───────────────────────────────────────────
// Filter IDs that are always visible and cannot be removed via the + menu.

export const LOCKED_FILTERS_BY_CATEGORY: Record<string, string[]> = {
  Human: ['department', 'status'],
  'Service Account': ['department', 'status'],
  Emergency: ['department', 'status'],
  Batch: ['department', 'status'],
  Communication: ['department', 'status'],
  Orphan: ['department', 'status'],
  'AI Agent': ['department', 'status'],
};

// Initial enabled filter IDs before API data loads (matches Human locked set)
export const DEFAULT_ENABLED_FILTER_IDS = ['department', 'status'];

export const EMPTY_SELECTIONS: Record<string, string[]> = {};
